import classNames from "classnames"
import { renderRichText } from "gatsby-source-storyblok"

import Callout from "@components/elements/V2/Callout"
import Typography, { TypographySize } from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import useTrustedByText, {
  TRUSTED_BY_BUSINESSES_TOKEN,
} from "@hooks/V2/useTrustedByText"
import { hasRichTextContent } from "@utils/richtext"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import { getTextColorClass } from "@utils/V2/color"
import interpolateTemplate from "@utils/V2/interpolateTemplate"
import React from "react"

export interface LayoutHeaderProps {
  size: "small" | "large"
  alignment?: "center" | "left"
  mobileAlignment?: "center" | "left"
  superscriptColor?: Color
  headingTextColor?: Color
  headingBodyColor?: Color
  headingGapSize?: "small" | "large"
  superscriptText?: string
  headingText?: string
  headingBodyText?: string
  headingBodyRichText?: Storyblok.StoryblokRichtextContent
  headingBodyClassName?: string
  highlightedText?: string
  highlightColor?: Color
  callout?: Storyblok.Callout
  calloutPosition?: "top" | "bottom"
  calloutTextColor?: Color
}

const SUPERSCRIPT_SIZES: Record<string, TypographySize> = {
  small: "subscript-lg",
  large: "subscript-lg",
}

const HEADING_SIZES: Record<string, TypographySize> = {
  small: "lead-lg",
  large: "h4",
}

const HEADING_BODY_SIZES: Record<string, TypographySize> = {
  small: "body-lg",
  large: "body-lg",
}

const LayoutHeader = ({
  size,
  alignment = "center",
  mobileAlignment = "left",
  superscriptText,
  superscriptColor = Color.Black,
  headingText,
  headingTextColor = Color.Black,
  headingBodyText,
  headingBodyRichText,
  headingBodyClassName,
  headingBodyColor = Color.Black,
  headingGapSize = "large",
  highlightedText,
  highlightColor,
  callout,
  calloutPosition = "bottom",
  calloutTextColor = Color.Black,
}: LayoutHeaderProps) => {
  const headingTextUsesTrustedByToken =
    headingText?.includes(TRUSTED_BY_BUSINESSES_TOKEN) ?? false
  const highlightedTextUsesTrustedByToken =
    highlightedText?.includes(TRUSTED_BY_BUSINESSES_TOKEN) ?? false

  const { text: resolvedHeadingText, highlightedText: resolvedTrustedCount } =
    useTrustedByText({
      template: headingText ?? "",
      valueKey: "trustedByNumBusinesses",
      isAnimated: false,
    })

  const finalHeadingText = headingTextUsesTrustedByToken
    ? resolvedHeadingText
    : headingText
  const finalHighlightedText = highlightedTextUsesTrustedByToken
    ? interpolateTemplate(highlightedText ?? "", {
        trustedByNumBusinesses: resolvedTrustedCount,
      })
    : highlightedText

  const hasHeadingBodyRichText = hasRichTextContent(headingBodyRichText)

  const calloutElement = callout && (
    <Callout
      {...callout}
      text={callout.text}
      highlightedWord={callout.highlightedWord}
      ctaLink={storyblokLinkToYocoLink(callout.ctaLink)}
      textSize="body-sm"
      highlightSize="body-md-fixed"
      color={calloutTextColor}
      classname={classNames(
        "m-auto",
        calloutPosition === "top" ? "my-14" : "mt-14"
      )}
    />
  )

  return (
    <div
      className={classNames(headingGapSize === "large" ? "mb-2xl" : "mb-24")}
    >
      {calloutPosition === "top" && calloutElement}

      {superscriptText && (
        <Typography
          color={superscriptColor}
          text={superscriptText}
          font="grotesk"
          weight="book"
          size={SUPERSCRIPT_SIZES[size]}
          className={classNames(
            "mb-8",
            mobileAlignment === "center" ? "text-center" : "text-left",
            alignment == "center" ? "sm:text-center" : "sm:text-left"
          )}
        />
      )}
      {finalHeadingText &&
        // TODO add highlighted text support to the typography component
        (finalHighlightedText && highlightColor ? (
          <h2
            className={classNames(
              mobileAlignment === "center" ? "text-center" : "text-left",
              alignment == "center" ? "sm:text-center" : "sm:text-left"
            )}
          >
            <Typography
              color={headingTextColor}
              text={finalHeadingText}
              font="grotesk"
              size={HEADING_SIZES[size]}
              weight="medium"
              highlightColor={highlightColor}
              highlightedText={finalHighlightedText}
              highlightSize={HEADING_SIZES[size]}
              highlightWeight="medium"
              element="span"
            />
          </h2>
        ) : (
          <Typography
            color={headingTextColor}
            text={finalHeadingText}
            font="grotesk"
            weight="medium"
            size={HEADING_SIZES[size]}
            element="h2"
            className={classNames(
              mobileAlignment === "center" ? "text-center" : "text-left",
              alignment == "center" ? "sm:text-center" : "sm:text-left"
            )}
          />
        ))}

      {hasHeadingBodyRichText ? (
        <div
          className={classNames(
            "text-responsive-body-lg mt-8 font-grotesk font-normal",
            "[&_a]:text-blue [&_a]:no-underline [&_b]:font-medium [&_strong]:font-medium",
            getTextColorClass(headingBodyColor),
            mobileAlignment === "center" ? "text-center" : "text-left",
            alignment == "center" ? "sm:text-center" : "sm:text-left",
            headingBodyClassName
          )}
          dangerouslySetInnerHTML={{
            __html: renderRichText(headingBodyRichText) ?? "",
          }}
        />
      ) : (
        headingBodyText && (
          <Typography
            color={headingBodyColor}
            text={headingBodyText}
            font="grotesk"
            weight="book"
            size={HEADING_BODY_SIZES[size]}
            className={classNames(
              "mt-8 opacity-70",
              mobileAlignment === "center" ? "text-center" : "text-left",
              alignment == "center" ? "sm:text-center" : "sm:text-left",
              headingBodyClassName
            )}
          />
        )
      )}

      {calloutPosition !== "top" && calloutElement}
    </div>
  )
}

export default LayoutHeader
